import { useReproduction } from '@/api/hooks';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody } from '@/components/ui/Card';
import { ClickableTableRow } from '@/components/ui/ClickableTableRow';
import { ListPagination } from '@/components/ui/ListPagination';
import { PageHeader } from '@/components/ui/PageHeader';
import { usePropertyScope } from '@/contexts/PropertyContext';
import { formatDate, eventTypeLabel } from '@/lib/format';
import { Dna } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export function ReproductionPage() {
  const { propertyId } = usePropertyScope();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useReproduction({
    property_id: propertyId ?? undefined,
    page,
  });
  const lastPage = data?.meta.last_page ?? 1;
  const needsFullStats = lastPage > 1;
  const { data: statsData } = useReproduction({
    property_id: needsFullStats ? (propertyId ?? undefined) : undefined,
    per_page: 1000,
  });

  useEffect(() => {
    setPage(1);
  }, [propertyId]);

  const rows = data?.reproduction ?? [];
  const statsRows = needsFullStats ? (statsData?.reproduction ?? []) : rows;
  const stats = useMemo(() => {
    const pregnant = statsRows.filter((r) => r.is_pregnant);
    return {
      total: data?.meta.total ?? statsRows.length,
      pregnant: pregnant.length,
      empty: statsRows.length - pregnant.length,
    };
  }, [data?.meta.total, statsRows]);

  return (
    <div>
      <PageHeader
        title="Reprodução"
        subtitle="Status reprodutivo das fêmeas — último diagnóstico"
      />

      {!propertyId ? (
        <Card><CardBody className="py-10 text-center text-manejai-bark/60">Selecione uma propriedade.</CardBody></Card>
      ) : isLoading ? (
        <div className="h-48 rounded-2xl bg-manejai-sand/40 animate-pulse" />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3 mb-6">
            <Card>
              <CardBody className="text-center">
                <p className="text-xs text-manejai-bark/60">Fêmeas com DG</p>
                <p className="font-display text-3xl font-semibold text-manejai-forest">{stats.total}</p>
              </CardBody>
            </Card>
            <Card>
              <CardBody className="text-center">
                <p className="text-xs text-manejai-bark/60">Prenhes</p>
                <p className="font-display text-3xl font-semibold text-emerald-700">{stats.pregnant}</p>
              </CardBody>
            </Card>
            <Card>
              <CardBody className="text-center">
                <p className="text-xs text-manejai-bark/60">Vazias</p>
                <p className="font-display text-3xl font-semibold text-manejai-bark/70">{stats.empty}</p>
              </CardBody>
            </Card>
          </div>

          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-manejai-sand text-left text-xs text-manejai-bark/60">
                    <th className="px-5 py-3 font-medium">Vaca</th>
                    <th className="px-5 py-3 font-medium">Touro</th>
                    <th className="px-5 py-3 font-medium">Cobertura</th>
                    <th className="px-5 py-3 font-medium">DG</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Parto previsto</th>
                    <th className="px-5 py-3 font-medium w-12" aria-hidden />
                  </tr>
                </thead>
                <tbody className="divide-y divide-manejai-sand/60">
                  {rows.map((r) => (
                    <ClickableTableRow
                      key={r.cow_id}
                      to={`/animais/${r.cow_id}`}
                      label={`Ver animal ${r.cow_internal_number}`}
                    >
                      <td className="px-5 py-3 font-medium text-manejai-forest">
                        {r.cow_internal_number}
                        {r.cow_name && <span className="text-manejai-bark/60 font-normal"> · {r.cow_name}</span>}
                      </td>
                      <td className="px-5 py-3">{r.bull_internal_number ?? '—'}</td>
                      <td className="px-5 py-3">
                        {r.cover_date ? (
                          <span>{formatDate(r.cover_date)} {r.cover_type && <span className="text-xs text-manejai-bark/50">({eventTypeLabel(r.cover_type)})</span>}</span>
                        ) : '—'}
                      </td>
                      <td className="px-5 py-3">{formatDate(r.diagnosis_date)}</td>
                      <td className="px-5 py-3">
                        <Badge tone={r.is_pregnant ? 'success' : 'default'}>
                          {r.is_pregnant ? 'Prenhe' : 'Vazia'}
                        </Badge>
                      </td>
                      <td className="px-5 py-3">{formatDate(r.expected_calving_date)}</td>
                    </ClickableTableRow>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-5 py-16 text-center text-manejai-bark/60">
                        <Dna className="h-8 w-8 mx-auto mb-2 text-manejai-sage opacity-50" />
                        Nenhum diagnóstico de gestação registrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <ListPagination
              page={page}
              lastPage={lastPage}
              summary={`${data?.meta.total ?? 0} fêmea(s) com DG`}
              onPageChange={setPage}
            />
          </Card>
        </>
      )}
    </div>
  );
}
